import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from '../firebase';
import { db } from '../firebase';
import { Download, Search, Filter, FileText } from 'lucide-react';
import { cn } from '../lib/utils';
import { formatCurrency, getDefaultCurrency } from '../lib/currency';
import SOAModal from './SOAModal';

const exportToCSV = (data: any[], filename: string) => {
  if (data.length === 0) return;
  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(','),
    ...data.map(row => headers.map(header => {
      const val = row[header];
      return typeof val === 'string' ? `"${val.replace(/"/g, '""')}"` : val;
    }).join(','))
  ];
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export default function CustomerBalancesView({ orgId, language, orgSettings, onNavigate }: { orgId: string, language: string, orgSettings?: any, onNavigate?: (tab: string, id?: string, extra?: any) => void }) {
  const [balances, setBalances] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [minBalance, setMinBalance] = useState<string>('');
  const [maxBalance, setMaxBalance] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSOA, setSelectedSOA] = useState<{ id: string, name: string } | null>(null);
  const currency = getDefaultCurrency(orgSettings);

  useEffect(() => {
    const fetchFirebaseBalances = async () => {
      const [contactsSnap, docsSnap, journalsSnap, accountsSnap] = await Promise.all([
        getDocs(query(collection(db, 'contacts'), where('orgId', '==', orgId))),
        getDocs(query(collection(db, 'documents'), where('orgId', '==', orgId), where('type', 'in', ['invoice', 'sales_receipt', 'quote']))),
        getDocs(query(collection(db, 'manual_journals'), where('orgId', '==', orgId))),
        getDocs(query(collection(db, 'chart_of_accounts'), where('orgId', '==', orgId)))
      ]);

      const contactsRaw = contactsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }) as any);
      const contacts = contactsRaw.filter(c => c.type?.toLowerCase() === 'customer' || c.type?.toLowerCase() === 'both');
      const docs = docsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }) as any);
      const journals = journalsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }) as any);
      const accounts = accountsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }) as any);
      const arAccountIds = accounts.filter(a => a.type === 'Accounts Receivable' || a.name.includes('Receivable')).map(a => a.id);

      return contacts.map(c => {
        let quoted = 0;
        let invoiced = 0;
        let paid = 0;
        let manualAdj = 0;

        docs.filter((d: any) => d.customerId === c.id || d.contactId === c.id).forEach((d: any) => {
          if (d.status !== 'Draft' && d.status !== 'Void') {
            if (d.type === 'quote') {
              quoted += (d.totalAmount || 0);
            } else if (d.type === 'invoice' || d.type === 'sales_receipt') {
              invoiced += (d.totalAmount || 0);
              paid += (d.paymentReceived || 0);
            }
          }
        });

        journals.forEach((j: any) => {
          if (j.status !== 'Draft' && j.lines) {
            (j.lines || []).forEach((line: any) => {
              if (line.contactId === c.id && arAccountIds.includes(line.accountId)) {
                manualAdj += (line.debit || 0);
                manualAdj -= (line.credit || 0);
              }
            });
          }
        });

        return {
          id: c.id,
          name: c.name || c.companyName || 'Unknown',
          email: c.email || '',
          quoted,
          invoiced,
          paid,
          manualAdj,
          balance: invoiced - paid + manualAdj
        };
      });
    };

    const fetchData = async () => {
      try {
        const token = localStorage.getItem('app_auth_token');
        if (token) {
          try {
            const response = await fetch(`/api/organizations/${encodeURIComponent(orgId)}/reports/aged-balances?kind=receivable`, {
              headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.ok) throw new Error(`Aged balances request failed: ${response.status}`);

            const payload = await response.json();
            const rows = Array.isArray(payload) ? payload : payload.rows || payload.data?.rows || payload.data || [];
            if (!Array.isArray(rows)) throw new Error('Unexpected aged balances response');

            const toNumber = (value: any) => {
              const number = typeof value === 'number' ? value : Number(String(value ?? '').replace(/[$,\s]/g, ''));
              return Number.isFinite(number) ? number : 0;
            };
            const firstNumber = (row: any, keys: string[]) => {
              const key = keys.find(candidate => row[candidate] !== undefined && row[candidate] !== null);
              return key ? toNumber(row[key]) : undefined;
            };
            const customerBalances = new Map<string, any>();

            rows.forEach((row: any, index: number) => {
              const id = String(row.contactId ?? row.contact_id ?? row.customerId ?? row.customer_id ?? row.id ?? row.contactName ?? row.contact_name ?? row.customerName ?? row.customer_name ?? `unknown-${index}`);
              const name = row.contactName ?? row.contact_name ?? row.customerName ?? row.customer_name ?? row.name ?? 'Unknown';
              const agingTotal = ['current', 'current_amount', 'days_1_30', 'days1_30', 'days_31_60', 'days31_60', 'days_61_90', 'days61_90', 'over_90', 'over90']
                .reduce((sum, key) => sum + toNumber(row[key]), 0);
              const balance = firstNumber(row, ['balance', 'total', 'total_due', 'totalDue', 'outstanding', 'outstanding_balance', 'outstandingBalance', 'amount_due', 'amountDue']) ?? agingTotal;
              const existing = customerBalances.get(id) || { id, name, email: row.email || '', quoted: 0, invoiced: 0, paid: 0, manualAdj: 0, balance: 0 };

              existing.quoted += toNumber(row.quoted);
              existing.invoiced += firstNumber(row, ['invoiced', 'invoice_total', 'invoiceTotal', 'totalAmount', 'total_amount']) ?? 0;
              existing.paid += firstNumber(row, ['paid', 'paidAmount', 'paid_amount']) ?? 0;
              existing.manualAdj += firstNumber(row, ['manualAdj', 'manual_adj', 'adjustments', 'adjustment']) ?? 0;
              existing.balance += balance;
              customerBalances.set(id, existing);
            });

            setBalances([...customerBalances.values()].sort((a, b) => b.balance - a.balance));
            return;
          } catch (err) {
            console.error('Local customer balances request failed:', err);
            setBalances([]);
            return;
          }
        }

        const customerBalances = await fetchFirebaseBalances();
        setBalances([...customerBalances].sort((a, b) => b.balance - a.balance));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orgId]);

  const filtered = balances.filter(b => {
    const matchesSearch = b.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMin = minBalance === '' || b.balance >= parseFloat(minBalance);
    const matchesMax = maxBalance === '' || b.balance <= parseFloat(maxBalance);
    return matchesSearch && matchesMin && matchesMax;
  });

  const totalBalance = balances.reduce((sum, b) => sum + b.balance, 0);

  const handleExport = () => {
    const data = filtered.map(b => ({
      Customer: b.name,
      Quoted: b.quoted,
      Invoiced: b.invoiced,
      Paid: b.paid,
      Adjustments: b.manualAdj,
      Balance: b.balance
    }));
    exportToCSV(data, `Customer_Balances_${new Date().toISOString().split('T')[0]}.csv`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{language === 'ar' ? 'أرصدة العملاء' : 'Customer Balances'}</h1>
          <p className="text-sm opacity-60">Manage and track accounts receivable by customer.</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-[#141414] text-[#E4E3E0] rounded-xl text-xs font-bold hover:opacity-90 transition-all"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          <div className="flex bg-[#141414] text-[#E4E3E0] px-6 py-2.5 rounded-2xl items-center gap-4">
            <div>
              <p className="text-[9px] uppercase tracking-widest font-bold opacity-60">Total Outstanding</p>
              <p className="text-lg font-bold font-mono">{formatCurrency(totalBalance, currency)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white/50 border border-[#141414] rounded-3xl p-6">
        <div className="space-y-4 mb-6">
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40" />
              <input 
                type="text" 
                placeholder="Search customers..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white border border-[#141414]/20 py-2 pl-10 pr-4 rounded-xl text-sm outline-none focus:border-[#141414] transition-colors"
              />
            </div>
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all border",
                showFilters ? "bg-[#141414] text-white border-[#141414]" : "bg-white border-[#141414]/10 hover:border-[#141414]"
              )}
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-[#141414]/5">
              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase opacity-40">Min Balance</label>
                <input 
                  type="number" 
                  value={minBalance}
                  onChange={(e) => setMinBalance(e.target.value)}
                  className="w-full bg-white border border-[#141414]/20 p-2 rounded-lg text-sm outline-none"
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase opacity-40">Max Balance</label>
                <input 
                  type="number" 
                  value={maxBalance}
                  onChange={(e) => setMaxBalance(e.target.value)}
                  className="w-full bg-white border border-[#141414]/20 p-2 rounded-lg text-sm outline-none"
                  placeholder="10000.00"
                />
              </div>
              <div className="flex items-end pb-1">
                <button 
                  onClick={() => { setMinBalance(''); setMaxBalance(''); }}
                  className="text-[10px] font-bold uppercase underline opacity-50 hover:opacity-100"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 opacity-50">Loading matches...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#141414]/10 text-xs uppercase tracking-widest opacity-60">
                  <th className="p-4 font-bold">Customer Name</th>
                  <th className="p-4 font-bold text-right">Quoted (Total)</th>
                  <th className="p-4 font-bold text-right">Invoiced (Total)</th>
                  <th className="p-4 font-bold text-right">Paid</th>
                  <th className="p-4 font-bold text-right">Adjustments (Journals)</th>
                  <th className="p-4 font-bold text-right text-primary">Balance Due</th>
                  <th className="p-4 w-12"></th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {filtered.map(b => (
                  <tr key={b.id} className="border-b border-[#141414]/5 hover:bg-[#141414]/5 transition-colors">
                    <td className="p-4 font-bold">
                      <button 
                        onClick={() => onNavigate?.('contacts', b.id)}
                        className="hover:underline text-left"
                      >
                        {b.name}
                      </button>
                    </td>
                    <td className="p-4 text-right font-mono">{formatCurrency(b.quoted, currency)}</td>
                    <td className="p-4 text-right font-mono">{formatCurrency(b.invoiced, currency)}</td>
                    <td className="p-4 text-right font-mono text-green-600">{formatCurrency(b.paid, currency)}</td>
                    <td className="p-4 text-right font-mono">{formatCurrency(b.manualAdj, currency)}</td>
                    <td className="p-4 text-right font-mono font-bold text-primary">
                      <button 
                        onClick={() => setSelectedSOA({ id: b.id, name: b.name })}
                        className="hover:underline text-right w-full"
                      >
                        {formatCurrency(b.balance, currency)}
                      </button>
                    </td>
                    <td className="p-4 text-right">
                      <button onClick={() => setSelectedSOA({ id: b.id, name: b.name })} className="p-2 text-[#141414]/50 hover:text-[#141414] transition-colors rounded-lg bg-[#141414]/5">
                        <FileText className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-sm opacity-50">No records found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedSOA && (
        <SOAModal 
          orgId={orgId} 
          contactId={selectedSOA.id} 
          contactName={selectedSOA.name} 
          type="Customer" 
          onClose={() => setSelectedSOA(null)} 
        />
      )}
    </div>
  );
}
